import { CheckOutlined, DownloadOutlined, EditOutlined, PlusCircleOutlined, QuestionOutlined, StopOutlined, DeleteOutlined, UploadOutlined, WarningOutlined } from "@ant-design/icons";
import { Form, Switch, DatePicker, TimePicker, Input, Slider, Select, Button, message, UploadProps, Tooltip, Divider, Upload, Menu, Modal, Spin, Card, Typography, Alert } from "antd";
import TextArea from "antd/lib/input/TextArea";
import { UploadFileStatus } from "antd/lib/upload/interface";
import moment from "moment";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GetAppFocusedTargetGroup } from "../../app/appSlice";
import { GetCachedPrompts, InvalidateEventsCache, GetCachedHolidays } from "../../app/cacheSlice";
import { GetUserIdToken, GetUserRoles } from "../../app/userSlice";
import { FormType } from "../../common/enums/FormType";
import { Language } from "../../common/enums/Language";
import { Role } from "../../common/enums/Role";
import { StandardAttributes } from "../../common/enums/StandardAttributes";
import { IRESTClient } from "../../common/interfaces/IRestClient";
import { Holiday } from "../../common/models/Holiday";
import ErrorPage from "./pages/ErrorPage";
import { StatusCode } from "../../common/enums/StatusCode";
import DaysofWeekSelector from "../../common/components/DaysofWeekSelector"
import { EventAction } from "../../common/enums/EventAction";
import { EventModelView } from "../../common/enums/EventModelView";
import { Event } from "../../common/models/Event";
import { ValidateStatus } from "antd/lib/form/FormItem";
import { SupervisorEditableAttributes } from "../../common/enums/SupervisorEditableAttributes";
import { ConfigFieldFormat } from "../../common/enums/ConfigFieldFormat";
//import EventUtil from "../../common/utils/EventUtil"
const { Option } = Select;
const { Text } = Typography;

export interface CHCFormItemProps {
    client: IRESTClient,
    itemKey: string,
    type: FormType,
    value: any
};

const CHCFormItem = (props: CHCFormItemProps) => {
    const { itemKey, type, value, client } = props;
    const roles = useSelector(GetUserRoles);
    const focusedTargetGroup = useSelector(GetAppFocusedTargetGroup);
    const idToken = useSelector(GetUserIdToken);
    const RelevantPromptsCache = useSelector(GetCachedPrompts)[focusedTargetGroup!];
    const [modelView, SetModelView] = useState<EventModelView | undefined>(undefined);
    const [modelIsVisible, SetModelIsVisible] = useState<boolean>(false);
    const [requestPending, SetRequestPending] = useState<boolean>(false);
    let formCallbacks: { [key in FormType]?: any } = {};
    const dispatch = useDispatch();
    const holidayCache = useSelector(GetCachedHolidays)[focusedTargetGroup!];
    /**
     * 
     * @param file Helper method to convert the wave file to base64.
     * @returns 
     */
    function GetBase64(file: any) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    }

    interface EventModelViewProps {
        client: IRESTClient,
        eventName?: string
    }

    /**
     * Takes in an array of moment strings and returns the database friendly equivalent.
     * @param hours Array of moment strings eg ["", ""]
     * @returns 
     */
    const ConvertHoursFromStringArray = (hours: string[]) => {
        let _hours = [];
        for(let index in hours) {
            let hour = hours[index];
            _hours.push(moment(hour, 'llll').format('hh:mm a'));
        }
        return _hours;
    }

    interface EventModelViewProps {
        client: IRESTClient,
        eventName?: string
    }

    /**
     * Generates a Card Form with input fields to create a new event
     * @returns A form to create a new event.
     */
    const AddEventModelView = (props: EventModelViewProps) => {
        let { client } = props;
        let [repeatEnabled, SetRepeatEnabled] = useState<boolean>(false);

        const eventPattern = ConfigFieldFormat.REACT_APP_EVENT_FILTER_PATTERN;
        if (eventPattern === undefined)
            return <ErrorPage errorMessage="Missing a event filter patern definition! Please try again or contact an administrator." />

        const OnFinish = async (event: Event) => {
            if (idToken) {
                SetRequestPending(true);
                event.hours = ConvertHoursFromStringArray(event.hours!);
                if(event.date !== undefined){
                    event.date = moment(event.date).format('MM/DD/YYYY'); 
                }
                if(event.prompt === '<speak></speak>'){
                    delete event.prompt;
                }
    
                /* let isOverlapExists : boolean = EventUtil.isEventExistsForSameTime(event, Object.entries(eventsCache));
                console.log(isOverlapExists);
                if(isOverlapExists){
                    message.error(`${event.name} Event is overlapping with some of the existing events.`);
                    SetRequestPending(false);
                    return;
                } */ 
                
                await client.AddEvent(focusedTargetGroup!, event, idToken)
                    .then((response) => {
                        if (response.status === StatusCode.SUCCESS) {
                            // Since we are not getting back the updated list in the response we need to force fresh the latest
                            SetModelView(undefined);
                            SetModelIsVisible(false);
                            SetRequestPending(false);
                            //Invalidate will force to fetch the events from DB
                            dispatch(InvalidateEventsCache());
                            message.success("Request succeeded");
                            return;
                        }
                        message.error(`${response.status}`);
                    })
                    .catch((error) => {
                        message.error(error);
                    })
            } else {
                message.error("Missing or invalid token!");
            }
        }
        return (
            <Card
                title={`Create a new event`}>
                <Text>Complete the form below to create a new event in the Self Service Portal.</Text>
                <Divider />
                <Form
                    onFinish={OnFinish}
                    layout="vertical"
                    name="createEvent"
                    size="large">
                    <Form.Item
                        tooltip="Provide a name for the event."
                        key='name'
                        name='name'
                        label='Name'
                        required
                        rules={[{
                            required: true,
                            message: `Event name is required. Event name should start with an uppercase letter and can only contain letters and underscores`,
                            pattern: new RegExp(eventPattern)
                        }]}>
                        <Input></Input>
                    </Form.Item>
                    <Form.Item
                        tooltip="Provide time range for the event."
                        key='hours'
                        name='hours'
                        label='Hours'
                        labelAlign={'left'}
                        initialValue={[moment(), moment()]}
                        required 
                        rules={[
                            {
                              required: true,
                              message: 'Please select hours',
                            },
                          ]}>
                        <TimePicker.RangePicker format="h:mm a" size="large" />
                    </Form.Item>
                    <Form.Item
                        tooltip="Is this event repeat?"
                        key='repeat'
                        name='repeat'
                        label='Repeat'
                        labelAlign={'left'}
                        labelCol={{ span: 4 }}
                        initialValue={repeatEnabled}
                        required>
                            <Switch checked={repeatEnabled} onChange={() => SetRepeatEnabled(!repeatEnabled)}  checkedChildren={'True'} unCheckedChildren={'False'} />
                    </Form.Item>
                    {!repeatEnabled && 
                    <Form.Item
                        tooltip="Select date of the event"
                        key='date'
                        name='date'
                        label='Date'
                        labelAlign={'left'}
                        labelCol={{span:4}}
                        initialValue={moment()} 
                        required
                        rules={[
                            {
                              required: true,
                              message: 'Date is required on which event will occure',
                            },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (value && value.isBefore(moment().startOf('day'))) {
                                  return Promise.reject('Please select a date that is not in the past');
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                        >
                            <DatePicker format={"MM/DD/YYYY"} size="large" />
                        </Form.Item>
                    }
                    {repeatEnabled &&
                    <Form.Item
                        tooltip="Select days of the event for which event will repeat"
                        key='days'
                        name='days'
                        label='Days'
                        labelAlign={'left'}
                        labelCol={{span:4}}
                        initialValue={value.days}
                        required
                        rules={[
                            {
                              required: true,
                              message: 'Select atleast one day for repeat event',
                            },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (value && value.length === 0) {
                                  return Promise.reject();
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}>
                            <DaysofWeekSelector disabled={false} key={'days'}></DaysofWeekSelector>
                        </Form.Item>
                    }
                    <Form.Item
                        tooltip="Select action for the event"
                        key='action'
                        name='action'
                        label='Action'
                        labelAlign={'left'}
                        labelCol={{span:4}} 
                        required 
                        rules={[
                            {
                              required: true,
                              message: 'Please select an Action',
                            },
                          ]}>
                            <Select style={{ width: 220 }} placeholder="Select an Action">
                                <Option value={EventAction.CLOSED}>{EventAction.CLOSED}</Option>
                                <Option value={EventAction.CONTINUE_TO_QUEUE}>{EventAction.CONTINUE_TO_QUEUE}</Option>
                            </Select>
                        </Form.Item>
                    <Form.Item
                        tooltip="Select prompt to play during event."
                        key='prompt'
                        name='prompt'
                        label='Prompt (Optional)'
                        labelAlign={'left'}
                        labelCol={{span:4}}
                        initialValue={'<speak></speak>'}>
                            <TextArea autoSize={{minRows:3, maxRows:8}}  />
                        </Form.Item>

                    <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                        <Button type="primary" htmlType="submit">Create Event</Button>
                    </Form.Item>
                </Form>
            </Card>);
    }

    /**
     * Generates a Card Form with a single input to confirm that you want to remove a event before doing so. Requires the user
     * to type in the event name as shown before the event will be removed.
     * @returns A form with an input field to validate that you want to remove the event before actually doing so.
     */
    const RemoveEventModelView = (props: EventModelViewProps) => {
        const [validateStatus, SetValidateStatus] = useState<ValidateStatus>('error');
        if (focusedTargetGroup === undefined)
            return <ErrorPage errorMessage={"Something went wrong when generating the form."} />;
        const OnChange = async (event: any) => {
            if (event.target.value === itemKey)
                SetValidateStatus('success');
        }
        const OnFinish = async (event: any) => {
            if (idToken) {
                SetRequestPending(true);
                await client.RemoveEvent(focusedTargetGroup!, new Event(event.event_name), idToken)
                    .then((response) => {
                        if (response.status === StatusCode.SUCCESS) {
                            SetModelView(undefined);
                            SetModelIsVisible(false);
                            dispatch(InvalidateEventsCache());
                            SetRequestPending(false);
                            message.success("Request succeeded");
                            return;
                        }
                        message.error(`${response.status}`);
                    })
                    .catch((error) => {
                        message.error(error);
                    });
            }
        }

        return (
            <Card
                title={`Removing event ${itemKey}`}
                bordered={false}>
                <Alert
                    showIcon
                    icon={<WarningOutlined />}
                    type="warning"
                    message={`Are you sure you want to remove this event ?`} />
                <Form
                    onFinish={OnFinish}
                    layout="horizontal"
                    name="removeEvent"
                    size="large">
                    <Divider />
                    <Form.Item
                        validateStatus={validateStatus}
                        help={`Enter the event name ${itemKey} to confirm you want to delete it.`}
                        key={1}
                        name='event_name'>
                        <Input onChange={OnChange} />
                    </Form.Item>
                    <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                        <Button disabled={validateStatus !== 'success'} danger type="primary" htmlType="submit">Remove event</Button>
                    </Form.Item>
                </Form>
            </Card>);
    }

    /**
     * Invoked when the value passed to the component is of type Prompt
     * @param itemKey The original key for the item.
     * @param value The value to be used as the default value
     * @param roles The users associated roles
     * @returns A JSX Form Item Element that displays this type of value in the correct format.
     */
    const OnPromptsFormItem = (itemKey: string, value: any, roles: Role[]) => {
        let label = `${itemKey.charAt(0).toUpperCase()}${itemKey.slice(1)}`
        let admin = roles?.includes(Role.DEVELOPER) || roles?.includes(Role.ADMINISTRATOR);
        let valueString = JSON.stringify(value);
        let [editEnabled, SetEditEnabled] = useState<boolean>(false);
        let uploadCallbacks: Record<UploadFileStatus, any> = {
            done: () => message.success('Upload complete!'),
            error: () => message.error('Upload failed!'),
            success: () => message.success('Upload complete!'),
            uploading: () => message.info('Uploading...'),
            removed: () => message.success('Removed')
        }
        // Wave file handling.
        if (valueString.startsWith("\"s3://") || valueString.startsWith("\"https://") || valueString.startsWith("\"http://")) {
            let valueArray = valueString.split('/');
            let _value = valueArray[valueArray.length - 1].replaceAll("\"", "");
            let payload: {[key: string]: any} = {};
            let uploadProps: UploadProps = {
                accept: ".wav",
                name: _value,
                showUploadList: false,
                onChange(info) {
                    if (info.file.status)
                        uploadCallbacks[info.file.status]();
                },
                async beforeUpload(file, FileList) {
                    await GetBase64(file).then(async data =>{
                        payload[itemKey] = data;
                        await client.UploadPromptWav(focusedTargetGroup!, 'undefined', payload, idToken!).then((apiResponse) => {
                            if (apiResponse.status !== undefined && apiResponse.status === StatusCode.SUCCESS) {
                                message.success('Upload complete!');
                            } else {
                                let errorMessage = apiResponse.errorMessage;
                                message.error(`Upload Failed! ${errorMessage === undefined ? '': errorMessage}`);
                            }
                        })
                    });
                    return false;
                },
            }
            return (
                <div style={{display:'flex', flexDirection:'column'}}>
                    <Divider orientation="left">{label}</Divider>
                    <div style={{display:'flex', flexDirection:'row', justifyContent:'right'}}>
                        <Upload {...uploadProps}>
                            <Button disabled={!admin} icon={<UploadOutlined />} type="link">Upload</Button>
                        </Upload>
                        <Button disabled type="link" icon={<DownloadOutlined />}>Download</Button>
                    </div>
                    <div>
                        {'S3 Url: '}
                        <TextArea autoSize={{maxRows:1}} defaultValue={value} disabled/>
                    </div>
                    <Divider/>
                </div>);
        }
        return (
            <>
                <Divider orientation="left">{label}</Divider>
                <div style={{display:'flex', flexDirection:'row'}}>
                <div style={{flex:'2'}}>
                    {editEnabled &&
                        <Form.Item 
                        labelAlign={'left'}
                        labelCol={{span:7}}
                        key={itemKey + 'prompt'}
                        initialValue={value} label={'Prompt'}
                        // eslint-disable-next-line no-template-curly-in-string
                        name={"${PROMPT}" + itemKey}>
                            <TextArea autoSize={{minRows:3, maxRows:8}} defaultValue={value} disabled={!admin || !editEnabled} />
                        </Form.Item>}
                    {!editEnabled &&
                        <div>
                            {'Prompt: '}
                            <TextArea autoSize={{minRows:3, maxRows:8}} defaultValue={value} disabled={!admin || !editEnabled} />
                        </div>}
                </div>
                <div style={{flex:'1'}}>
                    <Menu mode="horizontal" disabledOverflow={true}>
                        {editEnabled && <Menu.Item title="Save your changes to this value." key='saveButtonMI' icon={<CheckOutlined />}>
                                <Button key='saveButton' type='link' htmlType='submit'>Save</Button>
                            </Menu.Item>}
                        {!editEnabled && <Menu.Item title="Edit this value" key='editButtonMI' icon={<EditOutlined />}>
                                <Button key='editButton' type="link" onClick={() => SetEditEnabled(true)}>Edit</Button>
                            </Menu.Item>}
                        {editEnabled && <Menu.Item title="Cancel your changes to this value." key='cancelButtonMI' icon={<StopOutlined />}>
                            <Button key='cancelButton' danger type="link" onClick={() => SetEditEnabled(false)}>Cancel</Button>
                            </Menu.Item>}
                    </Menu>
                </div>
            </div>
            <Divider/>
        </>);
    }

    /**
     * Invoked when the value passed to the component is of type Holidays
     * @param itemKey The original key for the item.
     * @param value The value to be used as the default value
     * @param roles The users associated roles
     * @returns A JSX Form Item Element that displays this type of value in the correct format.
     */
    const OnHolidaysFormItem = (itemKey: string, value: any, roles: Role[]) => {
        let supervisor = roles?.includes(Role.SUPERVISOR) || roles?.includes(Role.DEVELOPER) || roles?.includes(Role.ADMINISTRATOR);
        let [editEnabled, SetEditEnabled] = useState<boolean>(false);
        let holiday: Holiday = value;
        let [closed, ToggleOpenClose] = useState<boolean>(holiday.closed);
        let tooltipMessage = closed? `Set Closed to false to enable special hours of operation for this holiday.` : `Set Closed to true to disable special hours of operation for this holiday.`;

        const checkDuplicateHolidayDate = (_ : any, newHolidayDate: moment.Moment) => {
            const holidayList = Object.entries(holidayCache);

            for (let index = 0; index < holidayList.length; index++) {
                const key = holidayList[index];
                //get holiday date from cache
                const cachedHolidayDateStr = key[0].trim();
                const newHolidayDateStr = newHolidayDate.format('MMDDYYYY');
                //get holiday name from cache
                const cachedHolidayStr = JSON.stringify(key[1]);
                let cachedHoliday = JSON.parse(cachedHolidayStr);
                const cachedHolidayName = cachedHoliday.value;

                if (newHolidayDateStr === cachedHolidayDateStr && cachedHolidayName !== holiday.value) {
                    return Promise.reject(new Error('There is a holiday exists with same date'));
                }
            }
            return Promise.resolve();
        }
        
        
        return(
            <>
                <Menu style={{float:'right'}} mode="horizontal" disabledOverflow={true}>
                    {editEnabled && <Menu.Item title="Save your changes to this value." key='saveButtonMI' icon={<CheckOutlined />}>
                            <Button key='saveButton' type='link' htmlType='submit'>Save</Button>
                        </Menu.Item>}
                    {!editEnabled && <Menu.Item title="Edit this value" key='editButtonMI' icon={<EditOutlined />}>
                            <Button key='editButton' type="link" onClick={() => SetEditEnabled(true)}>Edit</Button>
                        </Menu.Item>}
                    {editEnabled && <Menu.Item title="Cancel your changes to this value." key='cancelButtonMI' icon={<StopOutlined />}>
                        <Button key='cancelButton' danger type="link" onClick={() => SetEditEnabled(false)}>Cancel</Button>
                        </Menu.Item>}
                </Menu>
                <Divider orientation="left">{holiday.value}</Divider>
                <div style={{display:'flex', flexDirection:'row'}}>
                    <div style={{flex:'2'}}>
                        {!editEnabled && 
                            <div style={{display:'flex', flexDirection:'column'}}>
                                <div style={{display:'flex', flexDirection:'row', justifyContent:'space-evenly'}}>
                                    <div style={{flex:'1'}}>Date:</div>
                                    <div style={{flex:'6'}}>
                                        <DatePicker 
                                            defaultValue={moment(itemKey, 'MM/DD/YYYY')} format={"MM/DD/YYYY"}
                                            size="large" allowClear={false} disabled={!supervisor || !editEnabled} />
                                    </div>
                                </div><br/>

                                <div style={{display:'flex', flexDirection:'row', justifyContent:'space-evenly'}}>
                                    <div style={{flex:'1'}}>Closed:</div>
                                    <div style={{flex:'6'}}>
                                        <Switch
                                            defaultChecked={holiday.closed} disabled={!supervisor || !editEnabled}
                                            checkedChildren={'True'} unCheckedChildren={'False'} />
                                    </div>
                                </div><br/>
                                
                                <div style={{display:'flex', flexDirection:'row', justifyContent:'space-evenly'}}>
                                    <div style={{flex:'1'}}>Hours:</div>
                                    <div style={{flex:'6'}}>
                                        <TimePicker.RangePicker
                                            defaultValue={[moment(holiday.hours[0], 'h:mm a'), moment(holiday.hours[1], 'h:mm a')]}
                                            format="h:mm a" size="large" allowClear={false} disabled={!supervisor || !editEnabled} />
                                    </div>
                                </div><br/>
                            </div>}

                        {editEnabled &&
                            <>
                                <Form.Item
                                labelAlign={'left'}
                                labelCol={{span:4}}
                                key={itemKey + 'holidays'}
                                initialValue={moment(itemKey, 'MM/DD/YYYY')} 
                                label={'Date'}
                                name={`${holiday.value}_value`}
                                rules={[
                                   { validator: checkDuplicateHolidayDate }
                                ]}>
                                    <DatePicker format={"MM/DD/YYYY"} size="large" allowClear={false} disabled={!supervisor || !editEnabled} />
                                </Form.Item>

                                <Form.Item
                                labelAlign={'left'}
                                labelCol={{span:4}}
                                key={itemKey + 'closed'}
                                initialValue={holiday.closed}
                                label={'Closed'}
                                valuePropName='checked' 
                                name={`${holiday.value}_isOpen`}>
                                    <Switch 
                                    onChange={() => ToggleOpenClose(!closed)}
                                    defaultChecked={holiday.closed} disabled={!supervisor || !editEnabled} 
                                    checkedChildren={'True'} unCheckedChildren={'False'} />
                                </Form.Item>
                                
                                <Form.Item
                                tooltip={tooltipMessage}
                                labelAlign={'left'}
                                labelCol={{span:4}}
                                key={itemKey + '_hours'}
                                initialValue={[moment(holiday.hours[0], 'h:mm a'), moment(holiday.hours[1], 'h:mm a')]}
                                label={'Hours'}
                                name={`${holiday.value}_hours`}>
                                    <TimePicker.RangePicker 
                                    format="h:mm a"
                                    size="large"
                                    allowClear={false} disabled={!supervisor || !editEnabled || closed} />
                                </Form.Item>
                            </>}
                    </div>
                </div>
                <Divider/>
            </>);
    }

    /**
     * Invoked when the value passed to the component is of type Hours
     * @param itemKey The original key for the item.
     * @param value The value to be used as the default value
     * @param roles The users associated roles
     * @returns A JSX Form Item Element that displays this type of value in the correct format.
     */
    const OnHoursFormItem = (itemKey: string, value: any, roles: Role[]) => {
        let supervisor = roles?.includes(Role.SUPERVISOR) || roles?.includes(Role.DEVELOPER) || roles?.includes(Role.ADMINISTRATOR);
        let devOrAdmin = roles?.includes(Role.DEVELOPER) || roles?.includes(Role.ADMINISTRATOR);
        let [editEnabled, SetEditEnabled] = useState<boolean>(false);
        let repeatDefaultVal = value.hasOwnProperty('repeat') ? value.repeat : false;
        let [repeatEnabled, SetRepeatEnabled] = useState<boolean>(repeatDefaultVal);
        //render events
        if(value.action){
            let label = `${itemKey.charAt(0).toUpperCase()}${itemKey.slice(1)}`
            moment.updateLocale('en', {
                weekdaysMin: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]
              });
            return (
                <>
                        <div style={{ display: 'flex',  flexDirection: 'row'}}>
                            <div style={{flex: '2'}}>
                            <Divider orientation="left" >
                                <span>{label}</span>
                            </Divider>
                            </div>
                            <div style={{flex: '1', display: 'flex',  flexDirection: 'row'}}>
                                <Menu mode="horizontal" disabledOverflow={true} style={{border : 'none'}}>
                                    {editEnabled && <Menu.Item title="Save your changes to this value." key='saveButtonMI' icon={<CheckOutlined />}>
                                        <Button key='saveButton' type='link' htmlType='submit'>Save</Button>
                                    </Menu.Item>}
                                    {!editEnabled && supervisor && <Menu.Item title="Edit this value" key='editButtonMI' icon={<EditOutlined />}>
                                        <Button key='editButton' type="link" onClick={() => SetEditEnabled(true)}>Edit</Button>
                                    </Menu.Item>}
                                    {editEnabled && <Menu.Item title="Cancel your changes to this value." key='cancelButtonMI' icon={<StopOutlined />}>
                                        <Button key='cancelButton' danger type="link" onClick={() => {SetEditEnabled(false); }} >Cancel</Button>
                                    </Menu.Item>}
                                </Menu>
                                <Menu mode="horizontal" disabledOverflow={true} style={{border : 'none'}}>
                                    {devOrAdmin && <Menu.Item title="Delete the event" key='deleteButtonMI' icon={<DeleteOutlined />}>
                                        <Button key='deleteButton' danger type="link" onClick={() => { SetModelView(EventModelView.DELETE_EVENT); SetModelIsVisible(true) }}>Delete</Button>
                                    </Menu.Item>}
                                </Menu>
                            </div>
                        </div>
                        <Modal
                            width={'50%'}
                            footer={false}
                            closable={true}
                            destroyOnClose
                            onCancel={() => { SetModelView(undefined); SetModelIsVisible(false) }}
                            visible={modelIsVisible}>
                            <Spin
                                spinning={requestPending}>
                                <Card>
                                    {modelView === EventModelView.DELETE_EVENT && <RemoveEventModelView client={client} eventName={itemKey} />}
                                </Card>
                            </Spin>
                        </Modal>
                        
                    
                        <div style={{ display: 'flex', flexDirection: 'row' }}>
                            <div style={{ flex: '2' }}>
                                {editEnabled &&
                                    <>
                                    <Form.Item name={'data_type'} key={itemKey + 'data_type'} initialValue={'events'} hidden={true} />
                                    <Form.Item name={itemKey + '_event_name'} key={itemKey + 'event_name'} initialValue={itemKey} hidden={true} />
                                    <Form.Item
                                        labelAlign={'left'}
                                        labelCol={{ span: 4 }}
                                        key={itemKey + 'hours'}
                                        initialValue={[moment(value.hours[0], 'h:mm a'), moment(value.hours[1], 'h:mm a')]}
                                        label={'Hours:'} 
                                        name={itemKey + '_hours' }
                                        required 
                                        rules={[
                                            {
                                            required: true,
                                            message: 'Please select hours',
                                            },
                                        ]}>
                                        <TimePicker.RangePicker format="h:mm a" size="large" allowClear={false} disabled={!supervisor || !editEnabled} />
                                    </Form.Item>
                                    <Form.Item 
                                        labelAlign={'left'}
                                        labelCol={{ span: 4 }}
                                        key={itemKey + 'repeat'}
                                        initialValue={value.repeat}
                                        valuePropName='checked'
                                        label={'Repeat:'} 
                                        name={itemKey + '_repeat'}
                                        required>
                                            <Switch onChange={() => SetRepeatEnabled(!repeatEnabled)} disabled={!supervisor || !editEnabled} checkedChildren={'True'} unCheckedChildren={'False'} />
                                    </Form.Item>
                                    {!repeatEnabled && 
                                    <Form.Item
                                        labelAlign={'left'}
                                        labelCol={{span:4}}
                                        key={itemKey + 'date'}
                                        initialValue={value.date === "" ||  value.date !== undefined ? moment() : moment(value.date, 'MM/DD/YYYY')} 
                                        label={'Date:'}
                                        name={itemKey + '_date'}
                                        required
                                        rules={[
                                                {
                                                required: true,
                                                message: 'Date is required on which event will occure',
                                                },
                                                ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    if (value && value.isBefore(moment().startOf('day'))) {
                                                    return Promise.reject('Please select a date that is not in the past');
                                                    }
                                                    return Promise.resolve();
                                                },
                                                }),
                                            ]}
                                        >
                                            <DatePicker format={"MM/DD/YYYY"} size="large" allowClear={false} disabled={!supervisor || !editEnabled} />
                                        </Form.Item>
                                    }
                                    {repeatEnabled &&
                                    <Form.Item
                                        labelAlign={'left'}
                                        labelCol={{span:4}}
                                        key={itemKey + 'days'}
                                        initialValue={value.days} 
                                        label={'Days:'}
                                        name={itemKey + '_days'}
                                        required
                                        rules={[
                                            {
                                            required: true,
                                            message: 'Select atleast one day for repeat event',
                                            },
                                            ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (value && value.length === 0) {
                                                return Promise.reject();
                                                }
                                                return Promise.resolve();
                                            },
                                            }),
                                        ]}>
                                            <DaysofWeekSelector disabled={!supervisor || !editEnabled} key={itemKey}></DaysofWeekSelector>
                                        </Form.Item>
                                    }
                                    <Form.Item
                                        labelAlign={'left'}
                                        labelCol={{span:4}} 
                                        key={itemKey + 'action'}
                                        initialValue={value.action} 
                                        label={'Action:'} 
                                        name={itemKey + '_action'}
                                        required 
                                        rules={[
                                            {
                                            required: true,
                                            message: 'Please select an Action',
                                            },
                                        ]}>
                                            <Select style={{ width: 220 }} disabled={!supervisor || !editEnabled}>
                                                <Option value={EventAction.CLOSED}>{EventAction.CLOSED}</Option>
                                                <Option value={EventAction.CONTINUE_TO_QUEUE}>{EventAction.CONTINUE_TO_QUEUE}</Option>
                                            </Select>
                                        </Form.Item>
                                    <Form.Item 
                                        tooltip='Prompt to play during event'
                                        label='Prompt'
                                        labelAlign={'left'}
                                        labelCol={{span:4}}
                                        key={itemKey + 'prompt'}
                                        initialValue={value.prompt}
                                        name={itemKey + '_prompt'}>
                                            <TextArea autoSize={{minRows:3, maxRows:8}} disabled={!supervisor || !editEnabled} />
                                        </Form.Item>
                                    </>
                                }
                                    
                                {!editEnabled &&
                                    <>
                                        <div style={{paddingLeft : '40px', paddingTop: '20px'}}>
                                        <div>
                                            {'Hours: '}<TimePicker.RangePicker
                                                defaultValue={[moment(value.hours[0], 'h:mm a'), moment(value.hours[1], 'h:mm a')]}
                                                format="h:mm a" size="large" allowClear={false} disabled={!supervisor || !editEnabled} />
                                        </div><br/>
                                        <div>
                                                {'Repeat: '}
                                                <Switch defaultChecked={value.repeat} disabled={!supervisor || !editEnabled} checkedChildren={'True'} unCheckedChildren={'False'} />
                                        </div><br/>
                                        {!value.repeat && 
                                            <><div>
                                                {'Date: '}
                                                <DatePicker
                                                    defaultValue={moment(value.date, 'MM/DD/YYYY')} format={"MM/DD/YYYY"}
                                                    size="large" allowClear={false} disabled={!supervisor || !editEnabled} />
                                            </div><br /></>
                                        }
                                        {value.repeat && 
                                            <>
                                            <div style={{ display: 'flex', flex: '2', width: '60%'}}>
                                                <span>{'Days: '}</span>
                                                <DaysofWeekSelector value={value.days} disabled={!supervisor || !editEnabled} key={itemKey}></DaysofWeekSelector>
                                            </div><br /></>
                                        }
                                        <div>
                                                {'Action: '}
                                                <Select style={{ width: 220 }} defaultValue={value.action} disabled={!supervisor || !editEnabled} >
                                                    <Option value={EventAction.CLOSED}>{EventAction.CLOSED}</Option>
                                                    <Option value={EventAction.CONTINUE_TO_QUEUE}>{EventAction.CONTINUE_TO_QUEUE}</Option>
                                                </Select>
                                        </div><br/>
                                        <div style={{display: 'flex', flexDirection: 'row'}}>
                                            <div style={{flex:'1',display: 'flex', flexDirection: 'column'}}>
                                                <span>{'Prompt: '}</span>
                                                <TextArea style={{width:'70%'}} autoSize={{minRows:3, maxRows:8}} defaultValue={value.prompt} disabled={!supervisor || !editEnabled} />
                                            </div>
                                        </div><br/>
                                        </div>
                                    </>
                                }
                            </div>
                        </div>
                    <Divider />
                </>
            );
        } else if(itemKey === 'event_header'){ //render event header
            return (
                <>
                    <div style={{ display: 'flex',  flexDirection: 'row'}}>
                        <div style={{flex: '4'}}>
                        <Divider orientation="left" >
                            <div style={{ display: 'flex',  flexDirection: 'row'}}>
                            <h2>{value}</h2>
                            <div style={{paddingLeft : '10px', paddingTop: '5px'}}>
                                <Tooltip title={'Add Event'}>
                                    <Button key='addButton' shape="circle" type='primary'  style={{border : 'none'}} icon={<PlusCircleOutlined />} disabled={!supervisor} onClick={() => { SetModelView(EventModelView.CREATE_EVENT); SetModelIsVisible(true) }}></Button>
                                </Tooltip>
                            </div>
                            </div>
                        </Divider>
                        </div>
                    </div>
                    <Modal
                        width={'50%'}
                        footer={false}
                        closable={true}
                        destroyOnClose
                        onCancel={() => { SetModelView(undefined); SetModelIsVisible(false) }}
                        visible={modelIsVisible}>
                        <Spin
                            spinning={requestPending}>
                            <Card>
                                {modelView === EventModelView.CREATE_EVENT && <AddEventModelView client={client} />}
                            </Card>
                        </Spin>
                    </Modal>
                </>
            );
        //render hours
        }else{ 
            let label = `${itemKey.charAt(0).toUpperCase()}${itemKey.slice(1)}`
            return (
                <>
                    <Divider orientation="left">{label}</Divider><div style={{ display: 'flex', flexDirection: 'row' }}>
                            <div style={{ flex: '2' }}>
                                {editEnabled &&
                                    <Form.Item
                                        labelAlign={'left'}
                                        labelCol={{ span: 7 }}
                                        key={itemKey + 'hours'}
                                        initialValue={[moment(value[0], 'h:mm a'), moment(value[1], 'h:mm a')]}
                                        label={'Hours:'} name={itemKey}>
                                        <TimePicker.RangePicker format="h:mm a" size="large" allowClear={false} disabled={!supervisor || !editEnabled} />
                                    </Form.Item>}
                                {!editEnabled &&
                                    <div>
                                        {'Hours: '}<TimePicker.RangePicker
                                            defaultValue={[moment(value[0], 'h:mm a'), moment(value[1], 'h:mm a')]}
                                            format="h:mm a" size="large" allowClear={false} disabled={!supervisor || !editEnabled} />
                                    </div>}
                            </div>
                            <div style={{ flex: '1' }}>
                                <Menu mode="horizontal" disabledOverflow={true}>
                                    {editEnabled && <Menu.Item title="Save your changes to this value." key='saveButtonMI' icon={<CheckOutlined />}>
                                        <Button key='saveButton' type='link' htmlType='submit'>Save</Button>
                                    </Menu.Item>}
                                    {!editEnabled && <Menu.Item title="Edit this value" key='editButtonMI' icon={<EditOutlined />}>
                                        <Button key='editButton' type="link" onClick={() => SetEditEnabled(true)}>Edit</Button>
                                    </Menu.Item>}
                                    {editEnabled && <Menu.Item title="Cancel your changes to this value." key='cancelButtonMI' icon={<StopOutlined />}>
                                        <Button key='cancelButton' danger type="link" onClick={() => SetEditEnabled(false)}>Cancel</Button>
                                    </Menu.Item>}
                                </Menu>
                            </div>
                        </div>
                    <Divider />
                </>
            );
        }

    }

    /**
     * Invoked when the value passed to the component is of type Flags
     * @param itemKey The original key for the item.
     * @param value The value to be used as the default value
     * @param roles The users associated roles
     * @returns A JSX Form Item Element that displays this type of value in the correct format.
     */
    const OnFlagsFormItem = (itemKey: string, value: any, roles: Role[]) => {
        let label = `${itemKey.charAt(0).toUpperCase()}${itemKey.slice(1)}`;
        let admin = roles?.includes(Role.DEVELOPER) || roles?.includes(Role.ADMINISTRATOR);
        let supervisor = roles?.includes(Role.SUPERVISOR) || roles?.includes(Role.DEVELOPER) || roles?.includes(Role.ADMINISTRATOR);
        let [editEnabled, SetEditEnabled] = useState<boolean>(false);

        // Disable rendering of the API toggle for non developers.
        if(itemKey===StandardAttributes.CONNECT_API_ENABLED && !roles?.includes(Role.DEVELOPER))
            return;

        if (RelevantPromptsCache[value['relativePrompt']]) {
            let relativePrompt = RelevantPromptsCache[value['relativePrompt']];
            return (
                <>
                    <Divider orientation="left">{label}</Divider>
                    <div style={{display:'flex', flexDirection:'row'}}>
                        <div style={{flex:'2'}}>
                            {editEnabled &&
                                <>
                                    <Form.Item 
                                        labelAlign={'left'}
                                        labelCol={{span:7}}
                                        key={itemKey + 'flag'}
                                        initialValue={value['value']}
                                        valuePropName='checked'
                                        label={'Enabled'} name={itemKey}>
                                        <Switch disabled={!supervisor || !editEnabled} checkedChildren={'True'} unCheckedChildren={'False'} />
                                    </Form.Item>
                                    <Form.Item 
                                    tooltip='Any prompt that is effected by the toggle of this feature will be displayed here.'
                                    label='Prompt'
                                    labelAlign={'left'}
                                    labelCol={{span:7}}
                                    key={value['relativePrompt'] + 'relative-prompt'}
                                    initialValue={relativePrompt}
                                    // eslint-disable-next-line no-template-curly-in-string
                                    name={"${RELATIVE_PROMPT}" + value['relativePrompt']}>
                                        <TextArea autoSize={{minRows:3, maxRows:8}} defaultValue={relativePrompt} disabled={!admin || !editEnabled} />
                                    </Form.Item>
                                </>}

                            {!editEnabled &&
                                <>
                                    <div style={{ flex: '1', margin: `1%`, marginRight: 0 }}>
                                        {'Enabled: '}
                                        <Switch defaultChecked={value['value']} disabled={!supervisor || !editEnabled} checkedChildren={'True'} unCheckedChildren={'False'} />
                                    </div>
                                    <div style={{ flex: '2', paddingRight: `1%` }}>
                                        {'Prompt: '}
                                        <TextArea autoSize={{minRows:3, maxRows:8}} defaultValue={relativePrompt} disabled={!admin || !editEnabled} />
                                    </div>
                                </>}
                        </div>
                        <div style={{flex:'1'}}>
                            <Menu mode="horizontal" disabledOverflow={true}>
                                <Menu.Item><Tooltip overlay={value['description'] || 'Toggle this feature flag'}><QuestionOutlined/></Tooltip></Menu.Item>
                                {editEnabled && <Menu.Item title="Save your changes to this value." key='saveButtonMI' icon={<CheckOutlined />}>
                                        <Button key='saveButton' type='link' htmlType='submit'>Save</Button>
                                    </Menu.Item>}
                                {!editEnabled && <Menu.Item title="Edit this value" key='editButtonMI' icon={<EditOutlined />}>
                                        <Button key='editButton' type="link" onClick={() => SetEditEnabled(true)}>Edit</Button>
                                    </Menu.Item>}
                                {editEnabled && <Menu.Item title="Cancel your changes to this value." key='cancelButtonMI' icon={<StopOutlined />}>
                                    <Button key='cancelButton' danger type="link" onClick={() => SetEditEnabled(false)}>Cancel</Button>
                                    </Menu.Item>}
                            </Menu>
                        </div>
                    </div>
                    <Divider/>
                </>);
        }
        return (
            <>
                <Divider orientation="left">{label}</Divider>
                <div style={{display:'flex', flexDirection:'column'}}>
                    <div style={{display:'flex', flexDirection:'row', justifyContent:'right'}}>
                    <Menu mode="horizontal" disabledOverflow={true}>
                            <Menu.Item><Tooltip overlay={value['description'] || 'Toggle this feature flag'}><QuestionOutlined/></Tooltip></Menu.Item>
                            {editEnabled && <Menu.Item title="Save your changes to this value." key='saveButtonMI' icon={<CheckOutlined />}>
                                    <Button key='saveButton' type='link' htmlType='submit'>Save</Button>
                                </Menu.Item>}
                            {!editEnabled && <Menu.Item title="Edit this value" key='editButtonMI' icon={<EditOutlined />}>
                                    <Button key='editButton' type="link" onClick={() => SetEditEnabled(true)}>Edit</Button>
                                </Menu.Item>}
                            {editEnabled && <Menu.Item title="Cancel your changes to this value." key='cancelButtonMI' icon={<StopOutlined />}>
                                <Button key='cancelButton' danger type="link" onClick={() => SetEditEnabled(false)}>Cancel</Button>
                                </Menu.Item>}
                        </Menu>
                    </div>
                    {editEnabled && 
                        <div style={{ display:'flex', flexDirection:'row', justifyContent:'space-evenly'}}>
                            {'Enabled: '}
                            <Form.Item 
                            key={itemKey + 'flag'}
                            initialValue={value['value']}
                            valuePropName='checked' name={itemKey}>
                                <Switch defaultChecked={value['value']} disabled={!supervisor || !editEnabled} checkedChildren={'True'} unCheckedChildren={'False'} />
                            </Form.Item>
                        </div>}
                        
                    {!editEnabled && 
                        <div style={{ display:'flex', flexDirection:'row', justifyContent:'space-evenly'}}>
                            {'Enabled: '}<Switch 
                            defaultChecked={value['value']} 
                            disabled={!supervisor || !editEnabled} 
                            checkedChildren={'True'} unCheckedChildren={'False'} />
                        </div>}
                    <Divider />
                </div>
                <Divider/>
            </>);
    }

    /**
     * Invoked when the value passed to the component is of type Flags with MultiSelect
     * @param itemKey The original key for the item.
     * @param value The value to be used as the default value
     * @param roles The users associated roles
     * @returns A JSX Form Item Element that displays this type of value in the correct format.
     */
     const OnFlagsMultiFormItem = (itemKey: string, value: any, roles: Role[]) => {
        let label = `${itemKey.charAt(0).toUpperCase()}${itemKey.slice(1)}`;
        let description = <Tooltip overlay={value['description'] || 'Enable or disable.'}>{label}</Tooltip>
        let supervisor = roles?.includes(Role.SUPERVISOR) || roles?.includes(Role.DEVELOPER) || roles?.includes(Role.ADMINISTRATOR);
        let [editEnabled, SetEditEnabled] = useState<boolean>(false);
        // Disable rendering of the API toggle for non developers.
        if(itemKey===StandardAttributes.CONNECT_API_ENABLED && !roles?.includes(Role.DEVELOPER))
            return;
        return (
            <>
            <div style={{display:'flex', flexDirection:'row'}}>
                <div style={{flex:'1'}}>
                    {editEnabled && 
                        <Form.Item 
                        labelAlign={'left'}
                        labelCol={{span:7}}
                        key={itemKey + 'flag'}
                        initialValue={value['value']}
                        label={description} valuePropName='checked'
                        name={itemKey}>
                            <Switch defaultChecked={value['value']} disabled={!supervisor || !editEnabled} checkedChildren={'True'} unCheckedChildren={'False'} />
                        </Form.Item>}
                        
                    {!editEnabled && 
                        <div>
                            {label}:{`\t`}
                            <Switch defaultChecked={value['value']} disabled={!supervisor || !editEnabled} checkedChildren={'True'} unCheckedChildren={'False'} />
                        </div>}
                </div>
                <div style={{flex:'1'}}>
                    <Menu mode="horizontal" disabledOverflow={true}>
                        <Menu.Item><Tooltip overlay={value['description'] || 'Toggle this feature flag'}><QuestionOutlined/></Tooltip></Menu.Item>
                        {editEnabled && <Menu.Item title="Save your changes to this value." key='saveButtonMI' icon={<CheckOutlined />}>
                                <Button key='saveButton' type='link' htmlType='submit'>Save</Button>
                            </Menu.Item>}
                        {!editEnabled && <Menu.Item title="Edit this value" key='editButtonMI' icon={<EditOutlined />}>
                                <Button key='editButton' type="link" onClick={() => SetEditEnabled(true)}>Edit</Button>
                            </Menu.Item>}
                        {editEnabled && <Menu.Item title="Cancel your changes to this value." key='cancelButtonMI' icon={<StopOutlined />}>
                            <Button key='cancelButton' danger type="link" onClick={() => SetEditEnabled(false)}>Cancel</Button>
                            </Menu.Item>}
                    </Menu>
                </div>
            </div>
            <Divider/>
        </>);
    }

    /**
     * Invoked when the value passed to the component is of type Config
     * @param itemKey The original key for the item.
     * @param value The value to be used as the default value
     * @param roles The users associated roles
     * @returns A JSX Form Item Element that displays this type of value in the correct format.
     */
    const OnConfigFormItem = (itemKey: string, value: any, roles: Role[]) => {
        let label = `${itemKey.charAt(0).toUpperCase()}${itemKey.slice(1)}`
        let admin = roles?.includes(Role.DEVELOPER) || roles?.includes(Role.ADMINISTRATOR);
        let supervisor = roles?.includes(Role.DEVELOPER) || roles?.includes(Role.ADMINISTRATOR) || roles?.includes(Role.SUPERVISOR) ;
        let [editEnabled, SetEditEnabled] = useState<boolean>(false);
        return(
            <>
                <Divider orientation="left">{label}</Divider>
                <div style={{display:'flex', flexDirection:'row'}}>
                    <div style={{flex:'2'}}>
                        {itemKey === StandardAttributes.CALLBACK_OFFSET_HOURS &&
                            <>
                                {editEnabled?
                                    <Form.Item tooltip={"Set the difference in hours between when a callback queue is closed based on the parent queues original closure time. EG. An offset of .5 will close callback queus 30 minutes before the parent queues closing time."} key={itemKey + 'config'} initialValue={value} label={'Value'} name={itemKey}>
                                        <Slider value={value} max={3} min={0} disabled={!admin || !editEnabled} step={0.5}/>
                                    </Form.Item> : 
                                    <div>
                                        {'Value: '}
                                        <Slider value={value} max={3} min={0} disabled={!admin || !editEnabled} step={0.5}/>
                                    </div>}
                            </>}
                        {itemKey === StandardAttributes.BOT_PERCENTAGE_ALLOCATION &&
                            <>
                                {editEnabled?
                                    <Form.Item key={itemKey + 'config'} initialValue={value} label={'Value'} name={itemKey}>
                                        <Slider value={value} max={100} min={0} disabled={!admin || !editEnabled} />
                                    </Form.Item> : 
                                    <div>
                                        {'Value: '}
                                        <Slider value={value} max={100} min={0} disabled={!admin || !editEnabled} />
                                    </div>}
                            </>}
                        {itemKey === StandardAttributes.LANGUAGE &&
                            <>
                                {editEnabled?
                                    <Form.Item 
                                        key={itemKey + 'config'} initialValue={value} label={'Value'} name={itemKey}>
                                        <Select defaultValue={value} style={{ width: 220 }} disabled={!admin || !editEnabled}>
                                            {Object.keys(Language).map( key => {
                                               return process.env.REACT_APP_LANGUAGES?.split(', ').includes(key) ? <Option value={key}>{key}</Option> : <Option disabled value={key}>{key}</Option>
                                            })}
                                        </Select>
                                    </Form.Item> :
                                    <div>
                                        {'Value: '}
                                        <Select defaultValue={value} style={{ width: 220 }} disabled={!admin || !editEnabled}>
                                            {Object.keys(Language).map( key =><Option value={key}>{key}</Option> )}                                         
                                        </Select>
                                    </div>}
                            </>}
                        {itemKey === StandardAttributes.BOT_RETURNED_CALL_PRIORITY &&
                            <>
                            {editEnabled?
                                <Form.Item key={itemKey + 'config'} initialValue={'Value'} label={label} name={itemKey}>
                                    <Select defaultValue={value} style={{ width: 220 }} disabled={!admin || !editEnabled}>
                                        <Option value="default">Default</Option>
                                        <Option value="1">1</Option>
                                        <Option value="2">2</Option>
                                        <Option value="3">3</Option>
                                        <Option value="4">4</Option>
                                        <Option value="5">5</Option>
                                    </Select>
                                </Form.Item> :
                            <div>
                                {'Value: '}
                                <Select defaultValue={value} style={{ width: 220 }} disabled={!admin || !editEnabled}>
                                    <Option value="default">Default</Option>
                                    <Option value="1">1</Option>
                                    <Option value="2">2</Option>
                                    <Option value="3">3</Option>
                                    <Option value="4">4</Option>
                                    <Option value="5">5</Option>
                                </Select>
                            </div>}
                            </>}
                        {Object.values(SupervisorEditableAttributes).includes(itemKey as SupervisorEditableAttributes) &&
                            <>
                            {editEnabled?
                                  <Form.Item key={itemKey + 'Item'} initialValue={value} label={'Value'} name={itemKey}>
                                      <Input defaultValue={value} disabled={!supervisor || !editEnabled} />
                                  </Form.Item> :
                                  <div>
                                      {'Value: '}<Input defaultValue={value} disabled={!supervisor || !editEnabled} />
                                  </div>
                                  }
                            </>}
                        {itemKey !== StandardAttributes.BOT_RETURNED_CALL_PRIORITY && itemKey !== StandardAttributes.LANGUAGE && itemKey !== StandardAttributes.BOT_PERCENTAGE_ALLOCATION && itemKey!== StandardAttributes.CALLBACK_OFFSET_HOURS && !Object.values(SupervisorEditableAttributes).includes(itemKey as SupervisorEditableAttributes)  &&
                        <>
                            {editEnabled?
                            <Form.Item key={itemKey + 'Item'} initialValue={value} label={'Value'} name={itemKey}>
                                <Input defaultValue={value} disabled={!admin || !editEnabled} />
                            </Form.Item> :
                            <div>
                                {'Value: '}<Input defaultValue={value} disabled={!admin || !editEnabled} />
                            </div>
                            }
                        </>}
                        </div>
                    <div style={{flex:'1'}}>
                        <Menu mode="horizontal" disabledOverflow={true}>
                            {editEnabled && <Menu.Item title="Save your changes to this value." key='saveButtonMI' icon={<CheckOutlined />}>
                                    <Button key='saveButton' type='link' htmlType='submit'>Save</Button>
                                </Menu.Item>}
                            {!editEnabled && <Menu.Item title="Edit this value" key='editButtonMI' icon={<EditOutlined />}>
                                    <Button key='editButton' type="link" onClick={() => SetEditEnabled(true)}>Edit</Button>
                                </Menu.Item>}
                            {editEnabled && <Menu.Item title="Cancel your changes to this value." key='cancelButtonMI' icon={<StopOutlined />}>
                                <Button key='cancelButton' danger type="link" onClick={() => SetEditEnabled(false)}>Cancel</Button>
                                </Menu.Item>}
                        </Menu>
                    </div>
                </div>
                <Divider/>
            </>);
    }

    formCallbacks[FormType.CONFIG] = OnConfigFormItem;
    formCallbacks[FormType.PROMPTS] = OnPromptsFormItem;
    formCallbacks[FormType.HOLIDAYS] = OnHolidaysFormItem;
    formCallbacks[FormType.HOURS] = OnHoursFormItem;
    formCallbacks[FormType.HOURS_EVENTS] = OnHoursFormItem;
    formCallbacks[FormType.FLAGS] = OnFlagsFormItem;
    formCallbacks[FormType.MULTISELECT_FLAGS] = OnFlagsMultiFormItem;
    formCallbacks[FormType.MULTISELECT_HOURS] = OnHoursFormItem;
    formCallbacks[FormType.MULTISELECT_PROMPTS] = OnPromptsFormItem;

    try {
        return formCallbacks[type](itemKey, value, roles);
    } catch (error) {
        console.log(error);
        return(<><ErrorPage errorMessage="Unsupported form type!"/></>);
    }

};

export default CHCFormItem;